import type { ReactNode } from "react";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { useTranslation } from "react-i18next";

import { ChevronDownIcon, ThreeDotsIcon } from "~/ui";

interface DropDownOption {
  label: ReactNode;
  onClick?: () => void;
}

interface DropdownMenuProps {
  buttonAriaLabel?: string;
  options: DropDownOption[];
  type?: "ThreeDotsIcon" | "ChevronDown";
  maxHeight?: string;
  scrollThreshold?: number;
}

export const DropdownMenu = ({
  options,
  buttonAriaLabel,
  type = "ThreeDotsIcon",
  maxHeight = "320px",
  scrollThreshold = 4,
}: DropdownMenuProps) => {
  const { t } = useTranslation();

  const shouldScroll = options.length > scrollThreshold;
  const menuItemsClass = shouldScroll
    ? `w-64 rounded-2xl bg-surface-container-low text-secondary-20 dark:bg-surface-container-low-dark dark:text-primary overflow-y-auto`
    : `w-64 rounded-2xl bg-surface-container-low text-secondary-20 dark:bg-surface-container-low-dark dark:text-primary`;

  const menuItemsStyle = shouldScroll ? { maxHeight } : {};

  return (
    <Menu>
      <MenuButton
        aria-label={buttonAriaLabel ?? t("general.open_menu")}
        className="rounded-md px-3 py-1.5 data-[hover]:bg-neutral-variant-60 data-[open]:bg-neutral-variant-60"
      >
        {type === "ThreeDotsIcon" && (
          <ThreeDotsIcon
            aria-hidden={true}
            className="size-4 fill-neutral-70"
          />
        )}
        {type === "ChevronDown" && (
          <ChevronDownIcon
            aria-hidden={true}
            className="size-4 fill-neutral-70"
          />
        )}
      </MenuButton>
      <Transition
        enter="transition ease-out duration-75"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <MenuItems
          anchor="bottom end"
          className={menuItemsClass}
          style={menuItemsStyle}
        >
          {options.map(({ label, onClick }, idx) => (
            <MenuItem key={idx}>
              <button
                onClick={onClick}
                className="group flex w-full items-center border-l-3 border-transparent px-6 py-4 data-[focus]:border-info-70 data-[focus]:bg-neutral-80 dark:data-[focus]:bg-neutral-20"
              >
                {label}
              </button>
            </MenuItem>
          ))}
        </MenuItems>
      </Transition>
    </Menu>
  );
};
